import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useApi } from "./useApi";
import type { ProjectSummary } from "./types";
import { ProjectContext } from "./projectContext";
import TopBar from "./components/TopBar";
import Sidebar from "./components/Sidebar";
import TasksPage from "./pages/TasksPage";
import TaskDetailPage from "./pages/TaskDetailPage";
import AccessTokensPage from "./pages/AccessTokensPage";
import ProjectSetupPage from "./pages/ProjectSetupPage";
import AgentsPage from "./pages/AgentsPage";
import CostDashboardPage from "./pages/CostDashboardPage";
import SharingPage from "./pages/SharingPage";
import NewProjectPage from "./pages/NewProjectPage";

export default function App() {
  const auth = useAuth();
  const location = useLocation();

  if (auth.isLoading) {
    return <div className="center-screen">Loading…</div>;
  }
  if (auth.error) {
    return (
      <div className="center-screen">
        <div>
          <p className="error">Authentication error: {auth.error.message}</p>
          <button onClick={() => auth.signinRedirect()}>Try again</button>
        </div>
      </div>
    );
  }
  if (!auth.isAuthenticated) {
    // Trigger the sign-in redirect once. Storing the original path lets us
    // come back to it after Cognito redirects to /auth/callback.
    if (!location.pathname.startsWith("/auth/")) {
      sessionStorage.setItem("postLoginPath", location.pathname + location.search);
    }
    return <SignInPrompt />;
  }

  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallbackRedirect />} />
      <Route path="/projects/new" element={<AppShellWithoutProject><NewProjectPage /></AppShellWithoutProject>} />
      <Route path="/projects/:projectId/*" element={<ProjectRoutes />} />
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function SignInPrompt() {
  const auth = useAuth();
  useEffect(() => {
    auth.signinRedirect();
  }, [auth]);
  return <div className="center-screen">Redirecting to sign-in…</div>;
}

function AuthCallbackRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const target = sessionStorage.getItem("postLoginPath") ?? "/";
    sessionStorage.removeItem("postLoginPath");
    navigate(target, { replace: true });
  }, [navigate]);
  return <div className="center-screen">Signing in…</div>;
}

function RootRedirect() {
  const { call, hasToken } = useApi();
  const [projects, setProjects] = useState<ProjectSummary[] | undefined>();
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!hasToken) return;
    call<ProjectSummary[]>("/api/projects")
      .then(setProjects)
      .catch((e: Error) => setError(e.message));
  }, [call, hasToken]);

  if (error) return <div className="center-screen error">{error}</div>;
  if (!projects) return <div className="center-screen">Loading projects…</div>;
  if (projects.length === 0) {
    return <Navigate to="/projects/new" replace />;
  }
  return <Navigate to={`/projects/${projects[0].projectId}/tasks`} replace />;
}

function AppShellWithoutProject({ children }: { children: React.ReactNode }) {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const { call } = useApi();

  const refresh = async () => {
    setProjects(await call<ProjectSummary[]>("/api/projects"));
  };
  useEffect(() => {
    refresh().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="app">
      <TopBar projects={projects} currentProjectId={undefined} />
      <div className="body">
        <main className="main">{children}</main>
      </div>
    </div>
  );
}

function ProjectRoutes() {
  const { projectId = "" } = useParams();
  const [projects, setProjects] = useState<ProjectSummary[] | undefined>();
  const [error, setError] = useState<string | undefined>();
  const { call, hasToken } = useApi();

  const refresh = async () => {
    setProjects(await call<ProjectSummary[]>("/api/projects"));
  };

  useEffect(() => {
    if (!hasToken) return;
    refresh().catch((e: Error) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasToken]);

  if (error) return <div className="center-screen error">{error}</div>;
  if (!projects) return <div className="center-screen">Loading…</div>;

  const project = projects.find((p) => p.projectId === projectId);
  if (!project) return <Navigate to="/" replace />;

  return (
    <ProjectContext.Provider value={{ project, refreshProjects: refresh }}>
      <div className="app">
        <TopBar projects={projects} currentProjectId={project.projectId} />
        <div className="body">
          <Sidebar projectId={project.projectId} permission={project.permission} />
          <main className="main">
            <Routes>
              <Route index element={<Navigate to="tasks" replace />} />
              <Route path="tasks" element={<TasksPage />} />
              <Route path="tasks/:jiraIssueKey" element={<TaskDetailPage />} />
              <Route path="tokens" element={<AccessTokensPage />} />
              <Route path="setup" element={<ProjectSetupPage />} />
              <Route path="agents" element={<AgentsPage />} />
              <Route path="cost" element={<CostDashboardPage />} />
              <Route path="sharing" element={<SharingPage />} />
              <Route path="*" element={<Navigate to="tasks" replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </ProjectContext.Provider>
  );
}
